import type {
  AttachmentSimpleResponse,
  ComplianceStateEnum,
  InspectionDeviceEntryResponse,
  InspectionDeviceFieldValueRequest,
  InspectionDeviceFieldValueResponse,
  InspectionOfflineFieldSetResponse,
  InspectionScopeDeviceResponse,
} from 'src/api/ProactApiClient';

// Locally recorded state for one device, stored in the `:entry:{deviceId}`
// snapshot and merged over the pinned bundle at read time.
export interface LocalEntryState {
  complianceState?: ComplianceStateEnum;
  comments?: string;
  inspectedAt?: string;
  values: InspectionDeviceFieldValueResponse[];
  attachments: AttachmentSimpleResponse[];
}

// Composes a device entry from the pinned bundle (scope row + field set) merged
// with local recordings — mirrors the server `getDeviceEntry` shape exactly.
export const composeDeviceEntry = (
  device: InspectionScopeDeviceResponse,
  fieldSet: InspectionOfflineFieldSetResponse | undefined,
  local: LocalEntryState | undefined
): InspectionDeviceEntryResponse => ({
  inspectionDeviceId: device.inspectionDeviceId,
  deviceId: device.deviceId,
  deviceName: device.name ?? '',
  serialNumber: device.serialNumber,
  building: device.building,
  floor: device.floor,
  room: device.room,
  equipmentTypeId: device.equipmentTypeId,
  equipmentTypeName: device.equipmentTypeName ?? '',
  complianceState: local?.complianceState ?? device.complianceState,
  inspectedAt: local?.inspectedAt
    ? (new Date(local.inspectedAt) as unknown as Date)
    : undefined,
  comments: local?.comments,
  fieldFormDefinitionId: fieldSet?.fieldFormDefinitionId,
  effectiveVersion: fieldSet?.effectiveVersion,
  fields: fieldSet?.fields ?? [],
  values: local?.values ?? [],
  attachments: local?.attachments ?? [],
});

// Maps int-keyed record values to the response shape (adds invariantId +
// displayValue) via the pinned field set so re-seeded forms render identically.
export const recordValuesToResponse = (
  values: InspectionDeviceFieldValueRequest[],
  fieldSet: InspectionOfflineFieldSetResponse | undefined
): InspectionDeviceFieldValueResponse[] => {
  const byId = new Map((fieldSet?.fields ?? []).map(field => [field.id, field]));
  return values.map(value => {
    const field = byId.get(value.formFieldId);
    return {
      formFieldId: value.formFieldId,
      formFieldInvariantId: field?.invariantId ?? String(value.formFieldId),
      value: value.value,
      displayValue: value.value,
      unit: value.unit,
    };
  });
};
